import { useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import useProgressDashboard from '../hooks/useProgressDashboard';
import PathIcon from './PathIcon';

const paths = [
  {
    id: 'resources', label: 'Ressources', index: '01', icon: 'resources',
    title: 'Apprendre à votre rythme',
    text: 'Parcourez les cours, fiches et documents disponibles par matière.',
    action: 'Explorer les ressources', tone: 'teal',
  },
  {
    id: 'quizzes', label: 'Quiz', index: '02', icon: 'quizzes',
    title: 'Tester vos connaissances',
    text: 'Entraînez-vous avec des QCM et identifiez les notions à revoir.',
    action: 'Faire un quiz', tone: 'blue',
  },
  {
    id: 'simulations', label: 'Simulations', index: '03', icon: 'simulations',
    title: 'Vous mettre en situation',
    text: 'Choisissez un sujet pour vous exercer en conditions de concours.',
    action: 'Choisir une simulation', tone: 'amber',
  },
];

function JourneyPreview() {
  return (
    <div className="cb-prototype-journey">
      <div className="cb-prototype-journey-heading">
        <span className="cb-prototype-journey-spark" aria-hidden="true">✦</span>
        <div>
          <strong>Votre parcours vers la réussite</strong>
          <span>Une méthode simple, en trois étapes</span>
        </div>
      </div>
      <div className="cb-prototype-journey-steps">
        {[
          ['01', 'Découvrir', 'Ressources', 'resources'],
          ['02', 'S’exercer', 'Quiz', 'quizzes'],
          ['03', 'Se préparer', 'Simulations', 'simulations'],
        ].map(([number, title, subtitle, icon]) => (
          <div className="cb-prototype-journey-step" key={number}>
            <span className="cb-prototype-step-number">{number}</span>
            <PathIcon name={icon} />
            <strong>{title}</strong>
            <small>{subtitle}</small>
          </div>
        ))}
      </div>
      <p className="cb-prototype-journey-foot">
        À chacun son rythme, une étape après l’autre
      </p>
    </div>
  );
}

function PathCards({ onNavigate, connected = false }) {
  return (
    <section
      className={'cb-landing-section cb-paths' + (connected ? ' cb-paths--connected' : '')}
      aria-labelledby="cb-paths-title"
    >
      <div className="cb-section-head">
        <div>
          {!connected && <p className="cb-eyebrow">VOTRE ESPACE DE PRÉPARATION</p>}
          <h2 id="cb-paths-title">
            {connected ? 'Choisissez votre activité' : 'Trois façons de vous préparer'}
          </h2>
          {!connected && (
            <p>Des ressources pour apprendre, des quiz pour progresser et des simulations pour vous évaluer.</p>
          )}
        </div>
      </div>
      <div className="cb-path-grid">
        {paths.map((path) => (
          <article className={'cb-path-card cb-path-card--' + path.tone} key={path.id}>
            <div className="cb-path-card-top">
              <span className="cb-path-icon" aria-hidden="true"><PathIcon name={path.icon} /></span>
              <span className="cb-path-index">{path.index}</span>
            </div>
            <div className="cb-path-card-body">
              <p className="cb-path-label">{path.label}</p>
              <h3>{path.title}</h3>
              <p className="cb-path-description">{path.text}</p>
            </div>
            <button type="button" className="cb-path-link" onClick={() => onNavigate?.(path.id)}>
              {path.action}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Home({ onNavigate }) {
  const { currentUser, accessProfile } = useUser();
  const progressAccessKey = [
    accessProfile?.tier || 'free',
    accessProfile?.premiumExpiresAt || 'no-expiry',
  ].join(':');

  const { dashboard, loading: progressLoading } =
    useProgressDashboard(currentUser?.id, 100, progressAccessKey);

  const preparation = useMemo(() => {
    const activities = Array.isArray(dashboard?.activities) ? dashboard.activities : [];
    const summary = dashboard?.summary || {};
    const averageScore = Number(summary.averageScore);

    return {
      latest: activities[0] || null,
      averageScore: Number.isFinite(averageScore) && activities.length > 0
        ? averageScore.toFixed(1) + '/20'
        : '—',
      totalAttempts: Number.isFinite(Number(summary.totalAttempts))
        ? Number(summary.totalAttempts)
        : activities.length,
      difficultCount: Number.isFinite(Number(summary.difficultQuestionsCount))
        ? Number(summary.difficultQuestionsCount)
        : 0,
    };
  }, [dashboard]);

  const displayName =
    currentUser?.firstName ||
    currentUser?.pseudonym ||
    currentUser?.email?.split('@')[0] ||
    'candidat';

  const latestTitle = preparation.latest
    ? (preparation.latest.sujet || preparation.latest.titre || 'Session enregistrée')
    : null;

  return (
    <div className={'cb-landing cb-prototype-home' + (currentUser ? ' cb-prototype-home--connected' : '')}>
      {currentUser ? (
        <section className="cb-connected-command" aria-labelledby="cb-connected-title">
          <div className="cb-connected-greeting">
            <h1 id="cb-connected-title">Bonjour {displayName}</h1>
            <p>
              Continuez votre préparation là où elle est utile : apprendre une notion,
              vous entraîner ou vous mettre en situation.
            </p>
          </div>

          <div className="cb-connected-next">
            <div className="cb-connected-next-icon" aria-hidden="true">✦</div>
            <div className="cb-connected-next-copy">
              <span className="cb-connected-next-kicker">
                {latestTitle ? 'DERNIÈRE ACTIVITÉ' : 'PROCHAINE ÉTAPE'}
              </span>
              <strong>{latestTitle || 'Situez votre niveau avec un premier quiz'}</strong>
              <p>
                {latestTitle
                  ? 'Retrouvez le détail de votre activité et vos résultats dans votre espace.'
                  : 'Un entraînement court suffit pour commencer à identifier les notions à travailler.'}
              </p>
            </div>
            <button type="button" className="cb-connected-next-link"
              onClick={() => onNavigate?.('profile')}>
              Voir mon espace
            </button>
          </div>
        </section>
      ) : (
        <section className="cb-landing-hero" aria-labelledby="cb-landing-title">
          <div className="cb-hero-copy">
            <div className="cb-hero-eyebrow">
              <span className="cb-hero-status-dot" aria-hidden="true" />
              CONCOURSBF · BURKINA FASO
            </div>
            <h1 id="cb-landing-title">Préparez vos concours <em>avec méthode.</em></h1>
            <p className="cb-hero-description">
              Cours, QCM et simulations pour préparer les concours au Burkina Faso.
              Apprenez, entraînez-vous et progressez à votre rythme.
            </p>
            <div className="cb-hero-ctas">
              <button type="button" className="cb-cta cb-cta--primary"
                onClick={() => onNavigate?.('resources')}>
                Commencer à réviser
              </button>
              <button type="button" className="cb-cta cb-cta--outline"
                onClick={() => onNavigate?.('quizzes')}>
                Tester mes connaissances
              </button>
            </div>
            <p className="cb-hero-trust">
              <span aria-hidden="true">✓</span>
              Une plateforme pensée pour les candidats burkinabè.
            </p>
          </div>
          <div className="cb-hero-visual"><JourneyPreview /></div>
        </section>
      )}

      {currentUser && (
        <section className="cb-prototype-dashboard" aria-label="Synthèse de votre activité">
          <div className="cb-prototype-dashboard-head">
            <div>
              <h2>Votre progression, sans détour</h2>
            </div>
          </div>

          <div className="cb-prototype-stat-grid" aria-live="polite">
            <div className="cb-prototype-stat cb-prototype-stat--blue">
              <span>Entraînements</span>
              <strong>{progressLoading && !dashboard ? '—' : preparation.totalAttempts}</strong>
              <small>tentatives</small>
            </div>
            <div className="cb-prototype-stat cb-prototype-stat--green">
              <span>Score moyen</span>
              <strong>{preparation.averageScore}</strong>
              <small>sur vos activités</small>
            </div>
            <div className="cb-prototype-stat cb-prototype-stat--amber">
              <span>À revoir</span>
              <strong>{progressLoading && !dashboard ? '—' : preparation.difficultCount}</strong>
              <small>questions difficiles</small>
            </div>
          </div>

        </section>
      )}

      <PathCards onNavigate={onNavigate} connected={Boolean(currentUser)} />

      {!currentUser && (
        <section className="cb-progress-section" aria-labelledby="cb-progress-title">
          <div className="cb-progress-content">
            <p className="cb-eyebrow">UNE PRÉPARATION À VOTRE IMAGE</p>
            <h2 id="cb-progress-title">Chaque progrès compte.</h2>
            <p>
              Créez votre espace personnel pour retrouver vos entraînements
              et suivre vos progrès au fil de vos révisions.
            </p>
            <div className="cb-progress-actions">
              <button type="button" className="cb-cta cb-cta--dark" onClick={() => onNavigate?.('register')}>
                Créer mon compte
              </button>
              <button type="button" className="cb-text-link" onClick={() => onNavigate?.('login')}>
                J’ai déjà un compte
              </button>
            </div>
          </div>
          <div className="cb-progress-card">
            <span className="cb-prototype-progress-emblem" aria-hidden="true">✧</span>
            <h3>Votre espace, vos repères.</h3>
            <ul className="cb-prototype-benefits">
              <li>Retrouver votre activité</li>
              <li>Consulter vos scores</li>
              <li>Identifier les notions à revoir</li>
            </ul>
          </div>
        </section>
      )}

      {!currentUser && (
        <section className="cb-landing-bottom" aria-label="Informations et conseils">
          <div className="cb-bottom-note">
            <span className="cb-bottom-note-icon" aria-hidden="true">✺</span>
            <div>
              <h2>Avancez à votre rythme</h2>
              <p>Quelques révisions régulières valent mieux qu’une préparation précipitée.</p>
            </div>
          </div>
          <div className="cb-bottom-updates">
            <div>
              <p className="cb-eyebrow">RESTEZ INFORMÉ</p>
              <h2>Annonces de la plateforme</h2>
              <p>Retrouvez les communications publiées par ConcoursBF.</p>
            </div>
            <button type="button" className="cb-text-link"
              onClick={() => onNavigate?.('announcements')}>
              Consulter les annonces
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
